import { useStripe } from '@stripe/react-stripe-js';
import { PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js';
import { attachInstallIdToUrl } from '@youversion/analytics-youversion-nextjs';
import { useAuth, useSignInLink } from '@youversion/auth';
import { Box, Button } from '@youversion/design-system-components';
import DismissKeyboard from '@youversion/design-system-icons/lib/DismissKeyboard';
import Keyboard from '@youversion/design-system-icons/lib/Keyboard';
import Spinner from '@youversion/design-system-icons/lib/Spinner';
import { AlertBar } from 'components/layout/alert-bar';
import Footer from 'components/layout/footer';
import Header from 'components/layout/header';
import MobileHeader from 'components/layout/MobileHeader';
import ProcessingDialog from 'components/ui/dialogs/ProcessingDialog';
import ThankYouDialog from 'components/ui/dialogs/ThankYouDialog';
import GiveForm from 'components/views/giving/give-form';
import { getCookie, setCookie } from 'cookies-next';
import { format, isToday } from 'date-fns';
import { AnimatePresence, m } from 'framer-motion';
import { useTrackGiveCompletion } from 'hooks/analytics/useTrackGiveCompletion';
import { useMakePayment } from 'hooks/useMakePayment';
import { useQueryParams } from 'hooks/useQueryParams';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { authLocale } from 'src/utils/helpers/authLocale';
import { removeQueryParam } from 'src/utils/helpers/removerQueryParam';
import { sourceFromUserAgent } from 'src/utils/helpers/sourceFromUserAgent';
import { apiEnv, donationValidation } from 'utils/constants';
import { firebaseLogEvent } from 'utils/firebase';
import { setFlutterNav } from 'utils/flutter/setFlutterNav';
import { fallbackLng } from 'utils/i18n-settings';
import { useAmount } from 'utils/store/amount';
import { paymentRequest } from 'utils/store/availableWallets';
import { useCurrency } from 'utils/store/currency';
import { useFrequency } from 'utils/store/frequency';
import { useFund } from 'utils/store/fund';
import { usePaymentDate } from 'utils/store/paymentDate';
import { usePaymentMethod } from 'utils/store/paymentMethod';

import { useMakePaypalPaymentMutation } from '@/graphql/paymentMethods';

type EventValue =
  | 'Add Payment Method'
  | 'Give'
  | 'Select Frequency'
  | 'Select Fund'
  | 'Select Currency'
  | 'Show Keyboard'
  | 'Sign In';

interface DynamicButtonProps {
  eventValue: EventValue;
  green: boolean;
  handler: () => void;
  text: string;
}

const currencyErrorMessage = 'giving.messages.not_supported';

export default function Give() {
  const { i18n, t } = useTranslation('common');
  const { language: locale } = i18n;
  const { user } = useAuth();
  const stripe = useStripe();
  const { selectedFrequency } = useFrequency();
  const { getFundName, selectedFund } = useFund();
  const { selectedCurrency, getCurrencyId } = useCurrency();
  const { selectedAmount, setAmount } = useAmount();
  const { selectedPaymentDate } = usePaymentDate();
  const { getPaymentMethod, getPaymentMethodId, paymentMethodType } = usePaymentMethod();
  const { trackGiveCurrencyError, trackGiveSuccess } = useTrackGiveCompletion({ currencyErrorMessage });
  const { hide_nav: hideNav, token, utm_campaign, utm_content, utm_medium, utm_source } = useQueryParams();
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const [storedDonationSuccessMessage, setStoredDonationSuccessMessage] = useState('');
  const [storedDonationTitle, setStoredDonationTitle] = useState('');
  const [storedPaymentDate, setStoredPaymentDate] = useState('');
  const amountInputRef = useRef<HTMLInputElement>(null);
  const [isAmountInputFocused, setIsAmountInputFocused] = useState(false);

  const focusInput = useCallback(() => {
    amountInputRef.current?.focus();
    setIsAmountInputFocused(true);
  }, []);

  const [dynamicButton, setDynamicButton] = useState<DynamicButtonProps>({
    eventValue: 'Show Keyboard',
    green: false,
    handler: focusInput,
    text: t('giving.cta.to_give_enter_amount'),
  });
  const [processDateDialogOpen, setProcessDateDialogOpen] = useState(false);
  const [frequencyDialogOpen, setFrequencyDialogOpen] = useState(false);
  const [fundDialogOpen, setFundDialogOpen] = useState(false);
  const router = useRouter();
  const signInLink = useSignInLink(
    `${typeof window === 'undefined' ? '' : window.origin}/authenticate?from=${encodeURIComponent(router.asPath)}`,
    {
      appRef: 'portal',
      locale: authLocale(locale),
    }
  );

  const [paymentMethodDialogOpen, setPaymentMethodDialogOpen] = useState(false);
  const [thankYouDialogOpen, setThankYouDialogOpen] = useState(false);
  const [hasProcessedToken, setHasProcessedToken] = useState(false);

  const { mutate: addPaypalCheckout, isLoading: isRedirecting } = useMakePaypalPaymentMutation({
    onError: error => {
      reportError(`${error} [useMakePaypalPaymentMutation]`);
    },
    onSuccess: data => {
      const url = data.addPaypalCheckout?.url ?? '';
      window.location.assign(url);
    },
  });

  const { mutate: makePayment, isLoading: isMakePaymentLoading } = useMakePayment({
    onError: error => {
      console.error('Payment Error:', error);
      alert('Failed to submit payment');
      firebaseLogEvent('GivingError', { error: error as string });
    },
    onSuccess: (data, firebaseParams) => {
      const message = data.makePayment?.message;
      const title = data.makePayment?.title;
      const nextPaymentDate = data.makePayment?.nextPaymentDate;

      firebaseLogEvent('GivingGave', {
        amount: firebaseParams.amount,
        currency: firebaseParams.currency,
        frequency: firebaseParams.frequency,
        fund: firebaseParams.fund,
        logged_in: user ? 'true' : 'false',
        payment_method: firebaseParams.payment_method,
        start_date: firebaseParams.start_date,
        yv_id: user?.id,
      });

      setStoredDonationTitle(title ?? '');
      setStoredPaymentDate(nextPaymentDate ?? '');
      setStoredDonationSuccessMessage(message ?? '');
      setThankYouDialogOpen(true);
    },
  });

  const redirectToSignIn = useCallback(() => {
    if (window.FlutterAuthRoute) {
      window.FlutterAuthRoute.postMessage('');
    } else {
      window.location.href = attachInstallIdToUrl(signInLink);
    }
  }, [signInLink]);

  const postDonation = useCallback(
    async (paymentToken?: string) => {
      if (!user) {
        redirectToSignIn();
        return;
      }

      const stripeParams = {
        amount: selectedAmount,
        chargeDt: format(selectedPaymentDate, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX"),
        chargeTz: Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
        currencyId: getCurrencyId(selectedCurrency),
        firstName: user.firstName ?? '',
        frequencyId: selectedFrequency,
        fundId: selectedFund,
        lastName: user.lastName ?? '',
        transactionSource: sourceFromUserAgent(),
        ...(paymentToken ? { paymentToken: paymentToken } : { paymentMethodId: getPaymentMethodId() }),
        utmCampaign: utm_campaign ?? '',
        utmContent: utm_content ?? '',
        utmMedium: utm_medium ?? '',
        utmSource: utm_source ?? '',
      };

      const firebaseParams = {
        amount: selectedAmount,
        currency: selectedCurrency,
        frequency: selectedFrequency,
        fund: getFundName(selectedFund),
        payment_method: paymentMethodType?.toLowerCase() ?? 'unknown',
        start_date: format(selectedPaymentDate, 'yyyy-MM-dd'),
      };
      await makePayment({ payment: stripeParams }, firebaseParams);
    },
    [
      user,
      selectedAmount,
      selectedPaymentDate,
      getCurrencyId,
      selectedCurrency,
      selectedFrequency,
      selectedFund,
      getPaymentMethodId,
      utm_campaign,
      utm_content,
      utm_medium,
      utm_source,
      getFundName,
      paymentMethodType,
      makePayment,
      redirectToSignIn,
    ]
  );

  const postDonationViaWallet = useCallback(() => {
    if (!stripe) {
      return;
    }

    paymentRequest.update({
      currency: selectedCurrency.toLowerCase(),
      total: {
        amount: Math.round(parseFloat(selectedAmount) * 100), // Amount in cents
        label: 'YouVersion',
      },
    });
    paymentRequest.off('paymentmethod');
    paymentRequest.off('cancel');
    paymentRequest.once('paymentmethod', async (ev: PaymentRequestPaymentMethodEvent) => {
      postDonation(ev.paymentMethod.id);
      ev.complete('success');
    });
    paymentRequest.show();
  }, [postDonation, selectedAmount, selectedCurrency, stripe]);

  function blurInput() {
    amountInputRef.current?.blur();
    setIsAmountInputFocused(false);
  }

  useEffect(() => {
    focusInput();
  }, [focusInput]);

  const focusAndClearInput = useCallback(() => {
    focusInput();
    setAmount('');
  }, [focusInput, setAmount]);

  const handleGiveButtonClick = useCallback(() => {
    firebaseLogEvent('GivingButtonTap', {
      label: 'Giving Dynamic Button',
      screen: 'giving_form',
      value: dynamicButton.eventValue,
    });
    dynamicButton.handler();
  }, [dynamicButton]);

  const handleGive = useCallback(() => {
    const paymentMethod = getPaymentMethod();
    const payPalUnsupported = paymentMethod === 'paypal' && !token && selectedCurrency !== 'USD';
    if (paymentMethod === 'paymentRequest') {
      return postDonationViaWallet();
    } else if (payPalUnsupported) {
      // For now, only fire NAF completion if not in flutter land.
      if (!hideNav) {
        trackGiveCurrencyError();
      }
      alert(t(currencyErrorMessage));
      reportError(t(currencyErrorMessage));
      return null;
    } else if (paymentMethod === 'paypal' && !token) {
      const localeParam = locale === fallbackLng ? '' : `/${locale}`;
      const returnUrl = new URL(
        `${apiEnv === 'production' ? 'https://give.bible.com' : window.location.origin}${localeParam}/give`
      );
      returnUrl.search = window.location.search;
      const paypalParams = {
        amount: selectedAmount,
        cancelUrl: `${
          apiEnv === 'production' ? 'https://give.bible.com' : window.location.origin
        }${localeParam}/give/canceled`,
        chargeDt: format(selectedPaymentDate, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX"),
        chargeTz: Intl.DateTimeFormat().resolvedOptions().timeZone ?? '',
        frequencyId: selectedFrequency,
        fundId: selectedFund,
        returnUrl: returnUrl.toString(),
      };
      return addPaypalCheckout({ paypalCheckout: paypalParams });
    }
    // For now, only fire NAF completion if not in flutter land.
    if (!hideNav) {
      trackGiveSuccess();
    }

    return postDonation();
  }, [
    getPaymentMethod,
    token,
    selectedCurrency,
    hideNav,
    postDonation,
    postDonationViaWallet,
    t,
    trackGiveCurrencyError,
    locale,
    selectedAmount,
    selectedPaymentDate,
    selectedFrequency,
    selectedFund,
    addPaypalCheckout,
    trackGiveSuccess,
  ]);

  // -------------------------------------
  // Effects
  // -------------------------------------
  useEffect(() => {
    if (token && !hasProcessedToken && user) {
      removeQueryParam({ key: 'token', path: '/give', router });
      postDonation(token);
      setHasProcessedToken(true);
    }
  }, [hasProcessedToken, postDonation, router, token, user]);

  useEffect(() => {
    const anyDialogOpen = frequencyDialogOpen || fundDialogOpen || paymentMethodDialogOpen || processDateDialogOpen;
    setFlutterNav(!anyDialogOpen);
  }, [frequencyDialogOpen, fundDialogOpen, paymentMethodDialogOpen, processDateDialogOpen]);

  // -------------------------------------
  // Analytics Effect
  // -------------------------------------
  const hasEffectRun = useRef(false);
  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const checkPaymentMethod = () => {
      if (!hasEffectRun.current && paymentMethodType) {
        hasEffectRun.current = true;
        clearTimeout(timeout);
        firebaseLogEvent('GivingStarted', {
          amount: selectedAmount,
          currency: selectedCurrency,
          frequency: selectedFrequency,
          fund: getFundName(selectedFund),
          logged_in: user ? 'true' : 'false',
          payment_method: paymentMethodType?.toLowerCase() || '',
          start_date: format(selectedPaymentDate, 'yyyy-MM-dd'),
          yv_id: user?.id,
        });
        firebaseLogEvent('GivingScreenView', { screen: 'giving_form' });
      }
    };

    // if no payment method set after 3 seconds
    timeout = setTimeout(() => {
      if (!hasEffectRun.current) {
        hasEffectRun.current = true;
        firebaseLogEvent('GivingStarted', {
          amount: selectedAmount,
          currency: selectedCurrency,
          frequency: selectedFrequency,
          fund: getFundName(selectedFund),
          logged_in: user ? 'true' : 'false',
          payment_method: 'none',
          start_date: format(selectedPaymentDate, 'yyyy-MM-dd'),
          yv_id: user?.id,
        });
        firebaseLogEvent('GivingScreenView', { screen: 'giving_form' });
      }
    }, 3000);

    checkPaymentMethod();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethodType]);

  // -------------------------------------
  // Handles button states.
  // -------------------------------------
  useEffect(() => {
    if (!selectedAmount) {
      setDynamicButton({
        eventValue: 'Show Keyboard',
        green: false,
        handler: focusInput,
        text: t('giving.cta.to_give_enter_amount'),
      });
    } else if (parseFloat(selectedAmount) < donationValidation.minimumAmount || selectedAmount === '.') {
      setDynamicButton({
        eventValue: 'Show Keyboard',
        green: false,
        handler: focusAndClearInput,
        text: t('giving.cta.amount_must_be_greater_than_one'),
      });
      return;
    } else if (!user) {
      setDynamicButton({
        eventValue: 'Sign In',
        green: false,
        handler: redirectToSignIn,
        text: t('giving.cta.next_sign_in'),
      });
    } else if (!getPaymentMethod()) {
      setDynamicButton({
        eventValue: 'Add Payment Method',
        green: false,
        handler: () => setPaymentMethodDialogOpen(true),
        text: t('giving.cta.next_add_payment_method'),
      });
    } else {
      firebaseLogEvent('GivingReady', {
        amount: selectedAmount,
        currency: selectedCurrency,
        frequency: selectedFrequency,
        fund: getFundName(selectedFund),
        logged_in: 'true',
        payment_method: paymentMethodType?.toLowerCase() ?? 'unknown',
        start_date: format(selectedPaymentDate, 'yyyy-MM-dd'),
        yv_id: user.id,
      });
      if (isToday(selectedPaymentDate) && selectedFrequency === 'ONETIME') {
        setDynamicButton({
          eventValue: 'Give',
          green: true,
          handler: handleGive,
          text: t('giving.cta.give_amount_now', {
            amount: Intl.NumberFormat(locale, {
              currency: selectedCurrency,
              currencyDisplay: 'narrowSymbol',
              style: 'currency',
            }).format(parseFloat(selectedAmount)),
          }),
        });
      } else if (selectedFrequency === 'ONETIME') {
        setDynamicButton({
          eventValue: 'Give',
          green: true,
          handler: handleGive,
          text: t('giving.cta.schedule_amount_gift', {
            amount: Intl.NumberFormat(locale, {
              currency: selectedCurrency,
              currencyDisplay: 'narrowSymbol',
              style: 'currency',
            }).format(parseFloat(selectedAmount)),
          }),
        });
      } else if (isToday(selectedPaymentDate)) {
        setDynamicButton({
          eventValue: 'Give',
          green: true,
          handler: handleGive,
          text: t('giving.cta.give_amount_now_and_make_recurring', {
            amount: Intl.NumberFormat(locale, {
              currency: selectedCurrency,
              currencyDisplay: 'narrowSymbol',
              style: 'currency',
            }).format(parseFloat(selectedAmount)),
          }),
        });
      } else {
        setDynamicButton({
          eventValue: 'Give',
          green: true,
          handler: handleGive,
          text: t('giving.cta.make_amount_gift_recurring', {
            amount: Intl.NumberFormat(locale, {
              currency: selectedCurrency,
              currencyDisplay: 'narrowSymbol',
              style: 'currency',
            }).format(parseFloat(selectedAmount)),
          }),
        });
      }
    }
  }, [
    focusAndClearInput,
    handleGive,
    locale,
    redirectToSignIn,
    selectedAmount,
    selectedCurrency,
    selectedFrequency,
    selectedFund,
    selectedPaymentDate,
    t,
    user,
    paymentMethodType,
    getFundName,
    getPaymentMethod,
    focusInput,
  ]);

  // -------------------------------------
  // Calculates mobile keyboard offset.
  // -------------------------------------
  useEffect(() => {
    function getOffset(newKeyboardHeight: number, storedKeyboardHeight: number) {
      if (hideNav) {
        // the flutter webview doesn't understand keyboard height
        // so just return 1 to indicate keyboard is up
        return isAmountInputFocused ? 1 : 0;
      }
      if (isAmountInputFocused && newKeyboardHeight === 0) {
        // mobile keyboard animating up
        return storedKeyboardHeight;
      } else if (!isAmountInputFocused && newKeyboardHeight !== 0) {
        // mobile keyboard animating down
        return 0;
      } else {
        return newKeyboardHeight;
      }
    }

    function handleResize() {
      const newKeyboardHeight = window.innerHeight - (window.visualViewport?.height || 0);
      const storedKeyboardHeight = parseInt(getCookie('keyboardHeight')?.toString() || '0');
      if (newKeyboardHeight !== 0 && newKeyboardHeight !== storedKeyboardHeight) {
        setCookie('keyboardHeight', newKeyboardHeight);
      }
      setKeyboardOffset(getOffset(newKeyboardHeight, storedKeyboardHeight));
    }

    if (isMobile) {
      handleResize();
      window.onblur = blurInput;
      window.visualViewport?.addEventListener('resize', handleResize);
      window.visualViewport?.addEventListener('scroll', blurInput);
      window.addEventListener('scroll', blurInput);
    }

    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('scroll', blurInput);
      window.removeEventListener('scroll', blurInput);
    };
  }, [hideNav, isAmountInputFocused]);

  const giveButtonClassName = `w-full plb-2 flex items-center justify-center rounded-4 text-text-dark ${
    dynamicButton.green ? 'bg-green-30' : 'bg-canvas-dark dark:bg-canvas-light dark:text-text-light'
  } disabled:opacity-50 disabled:bg-gray-10 dark:disabled:bg-gray-40 disabled:text-gray-50 dark:disabled:text-white`;

  const GiveButtonComponent = useMemo(
    () => (
      <button
        className={giveButtonClassName}
        data-testid='giveButton'
        disabled={isMakePaymentLoading || isRedirecting}
        onClick={handleGiveButtonClick}
      >
        {isMakePaymentLoading || isRedirecting ? <Spinner dur={0.9} /> : dynamicButton.text}
      </button>
    ),
    [dynamicButton.text, giveButtonClassName, handleGiveButtonClick, isMakePaymentLoading, isRedirecting]
  );

  return (
    <Box
      className={`${
        isMobile
          ? `absolute inset-0 flex flex-col justify-between transition-height duration-300 ease-in-out overflow-hidden`
          : ''
      }`}
      style={{ bottom: `${hideNav ? -1 : keyboardOffset}px` }}
    >
      {hideNav ? <MobileHeader className='p-2' /> : <Header className='p-2 z-10' onSignInClick={redirectToSignIn} />}
      <Box className={`p-2 ${isMobile ? 'flex flex-col grow overflow-scroll' : ''}`}>
        <AlertBar />
        <GiveForm
          amountInputRef={amountInputRef}
          blurInput={blurInput}
          focusInput={focusInput}
          frequencyDialogOpen={frequencyDialogOpen}
          fundDialogOpen={fundDialogOpen}
          isAmountInputFocused={isAmountInputFocused}
          onGivingMethodClick={
            !user
              ? () => {
                  if (!user) {
                    redirectToSignIn();
                  }
                }
              : null
          }
          paymentMethodDialogOpen={paymentMethodDialogOpen}
          processDateDialogOpen={processDateDialogOpen}
          screen='giving_form'
          setFrequencyDialogOpen={setFrequencyDialogOpen}
          setFundDialogOpen={setFundDialogOpen}
          setPaymentMethodDialogOpen={setPaymentMethodDialogOpen}
          setProcessDateDialogOpen={setProcessDateDialogOpen}
        />
        <AnimatePresence>
          {isMobile && keyboardOffset <= 0 ? (
            <m.div
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              transition={{ ease: 'easeInOut' }}
            >
              <Footer screen='giving_form' />
            </m.div>
          ) : null}
        </AnimatePresence>
      </Box>
      {isMobile ? (
        <Box className='relative rounded-bs-2'>
          <Box className='w-full h-1 bg-canvas-secondary-light dark:bg-canvas-dark opacity-90 blur-sm relative -mbs-0.5 z-2' />
          <Box className='flex flex-row gap-2 bg-canvas-light dark:bg-canvas-secondary-dark p-2 rounded-bs-2 z-10'>
            {GiveButtonComponent}
            <Button
              className='p-2 rounded-full'
              onClick={keyboardOffset > 0 ? blurInput : focusInput}
              variant='secondary'
            >
              {keyboardOffset > 0 ? <DismissKeyboard /> : <Keyboard />}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box className='pli-2'>{GiveButtonComponent}</Box>
      )}
      {!isMobile ? <Footer screen='giving_form' /> : null}
      {isMakePaymentLoading ? <ProcessingDialog key='processing' /> : null}
      <AnimatePresence>
        {thankYouDialogOpen ? (
          <ThankYouDialog
            nextPaymentDate={storedPaymentDate}
            onClose={() => setThankYouDialogOpen(false)}
            successMessage={storedDonationSuccessMessage}
            successTitle={storedDonationTitle}
          />
        ) : null}
      </AnimatePresence>
    </Box>
  );
}
